/**
 * Appointments.tsx — PlatformOwner-only role appointment management (PC-F19), built on the Kombi
 * design system (kombi/admin/chrome) and mounted as an in-place modal panel over AdminHome
 * (AppointmentsPanel), rather than the old full-page navy screen. Grants/revokes Reviewer/
 * ChampionRole/SWATMember/Manager/PlatformOwner. Person lookup reuses the EXISTING global-search
 * suggest endpoint (fetchSuggestions) rather than requiring a raw UPN paste, unlike the dev CLI
 * (scripts/grantRole.ts) this replaces as a product surface.
 *
 * The panel fetches its own data on open via useOverlayData (AdminHome does not preload it) and, after
 * a grant/revoke, calls reload() to refresh in place instead of a router revalidation. The still-
 * registered /admin/appointments route renders through a thin Appointments() wrapper that opens the
 * same panel over a blank backdrop for the deep-link path.
 *
 * Provisional engineering UI — matches the other admin governance panels' own posture.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { AppointedRole } from '@trailblazer/shared';
import { APPOINTED_ROLES } from '@trailblazer/shared';
import { apiGet, apiPost, apiDelete, type LiveAppointment } from './api';
import { fetchSuggestions } from '../search/api';
import {
  EmptyState,
  ErrorLine,
  KombiAdminOverlay,
  Notice,
  NotPermitted,
  PrimaryAction,
  ProvisionalNote,
  QuietAction,
  SectionHeader,
  Spinner,
} from '../kombi/admin/chrome';
import { useOverlayData, type ReturnFocusRef } from './overlay';

export type AppointmentsLoaderData =
  | { state: 'signedOut' }
  | { state: 'forbidden' }
  | { state: 'ok'; appointments: LiveAppointment[] };

export async function appointmentsLoader(): Promise<AppointmentsLoaderData> {
  const res = await apiGet<{ appointments: LiveAppointment[] }>('/api/admin/appointments');
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') {
    if (res.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/admin/appointments failed (${res.status})`);
  }
  return { state: 'ok', appointments: res.body.appointments };
}

interface PersonMatch {
  userId: string;
  name: string;
  jobTitle: string | null;
  department: string | null;
}

/** Type-ahead person picker, reusing the global search field's own suggest endpoint. */
function PersonPicker({
  onPick,
  selected,
  inputId,
}: {
  onPick: (person: PersonMatch | null) => void;
  selected: PersonMatch | null;
  /** Ties the search field to the "Person" <label> in the parent form (accessible name). */
  inputId?: string;
}) {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<PersonMatch[]>([]);
  const [searching, setSearching] = useState(false);

  async function search(q: string) {
    setQuery(q);
    onPick(null);
    if (q.trim().length < 2) {
      setMatches([]);
      return;
    }
    setSearching(true);
    try {
      const { people } = await fetchSuggestions(q);
      setMatches(
        people.map((p) => ({
          userId: p.userId,
          name: p.name,
          jobTitle: p.jobTitle,
          department: p.department,
        })),
      );
    } finally {
      setSearching(false);
    }
  }

  if (selected) {
    return (
      <p className="kombi-admin-row-actions">
        <span>
          {selected.name}
          {selected.jobTitle ? ` · ${selected.jobTitle}` : ''}
        </span>
        <QuietAction onClick={() => onPick(null)}>Change</QuietAction>
      </p>
    );
  }

  return (
    <div>
      <input
        id={inputId}
        type="text"
        value={query}
        placeholder="Search for a person by name…"
        onChange={(e) => void search(e.target.value)}
      />
      {searching && <p className="kombi-field-hint">searching…</p>}
      {matches.length > 0 && (
        <ul className="kombi-admin-rows">
          {matches.map((m) => (
            <li key={m.userId}>
              <QuietAction onClick={() => onPick(m)}>
                {m.name}
                {m.department ? ` — ${m.department}` : ''}
              </QuietAction>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The grant form: pick a person, pick a role, grant — with the PlatformOwner-transfer confirmation. */
function GrantForm({ onGranted }: { onGranted: () => void }) {
  const [person, setPerson] = useState<PersonMatch | null>(null);
  const [role, setRole] = useState<AppointedRole>('Reviewer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transferConfirm, setTransferConfirm] = useState<{ currentOwnerName: string } | null>(null);

  async function grant(confirmTransfer = false) {
    if (!person) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiPost<{ ok: true; outcome: 'granted' | 'already_held' }>(
        '/api/admin/appointments',
        { userId: person.userId, role, confirmTransfer },
      );
      if (res.kind === 'ok') {
        setTransferConfirm(null);
        setPerson(null);
        onGranted();
        return;
      }
      if (
        res.kind === 'error' &&
        res.status === 409 &&
        res.code === 'owner_transfer_needs_confirmation'
      ) {
        const currentOwnerName = (res.detail as { currentOwnerName?: string } | undefined)
          ?.currentOwnerName;
        setTransferConfirm({ currentOwnerName: currentOwnerName ?? 'the current owner' });
        return;
      }
      setError(`Could not grant (${res.kind === 'error' ? res.status : 'unauthorized'}).`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kombi-dialog-stack">
      {/* The two fields are their own block so `.kombi-field`'s own 24px rhythm applies between them
          (and `:first-child` zeroes the first) instead of stacking on the dialog stack's gap. */}
      <div>
        {/* An explicit htmlFor rather than the wrapping <label> the Role field uses: PersonPicker's
            "selected" branch renders no input at all, so a wrapping label would associate with
            nothing (jsx-a11y/label-has-associated-control). */}
        <div className="kombi-field">
          <label htmlFor="appoint-person">Person</label>
          <PersonPicker selected={person} onPick={setPerson} inputId="appoint-person" />
        </div>
        <label className="kombi-field">
          <span>Role</span>
          <select
            id="appoint-role"
            value={role}
            onChange={(e) => setRole(e.target.value as AppointedRole)}
          >
            {APPOINTED_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <ErrorLine>{error}</ErrorLine>}

      {transferConfirm ? (
        <div className="kombi-prose" role="group" aria-label="Confirm platform owner transfer">
          <p>
            <strong>
              {transferConfirm.currentOwnerName} currently holds PlatformOwner and will be revoked.
            </strong>{' '}
            Are you sure?
          </p>
          <div className="kombi-admin-row-actions">
            <QuietAction onClick={() => setTransferConfirm(null)} disabled={busy}>
              Cancel
            </QuietAction>
            <PrimaryAction onClick={() => void grant(true)} disabled={busy}>
              Yes, transfer ownership
            </PrimaryAction>
          </div>
        </div>
      ) : (
        <div className="kombi-admin-row-actions">
          <PrimaryAction onClick={() => void grant(false)} disabled={busy || !person}>
            {busy ? 'Granting…' : 'Grant'}
          </PrimaryAction>
        </div>
      )}
    </div>
  );
}

/** One live appointment row, with a revoke action and its own PC-F18 confirmation. */
function AppointmentRow({
  appointment,
  onRevoked,
}: {
  appointment: LiveAppointment;
  onRevoked: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function revoke() {
    setBusy(true);
    setError(null);
    try {
      // `<null>`, not `<{ ok: true }>`: the route answers 204 with NO body (routes/appointments.ts),
      // so apiDelete's normaliser hands back `body: null`. Claiming an `{ ok: true }` here typed a
      // value that never arrives — harmless only because nothing reads it, and the house convention
      // for a bodiless write is the honest one (see apiPost<null> in learning/api.ts).
      const res = await apiDelete<null>(
        `/api/admin/appointments/${encodeURIComponent(appointment.appointmentId)}`,
      );
      if (res.kind === 'ok') {
        onRevoked();
        return;
      }
      if (res.kind === 'error' && res.status === 409 && res.code === 'sole_owner_blocked') {
        setError(
          'This is the only PlatformOwner appointment — grant it to someone else first to transfer it, rather than leaving the platform with none.',
        );
        return;
      }
      setError(`Could not revoke (${res.kind === 'error' ? res.status : 'unauthorized'}).`);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <li>
      {/* The record's text is the row's shrinkable side, and the confirm PROMPT sits with it rather
          than in the action cluster: `.kombi-admin-row-actions` is `flex: 0 0 auto`, so a long
          display name or UPN (a single unbroken user@tenant.onmicrosoft.com token) in there would
          push past the panel's edge — the "text comes outside of the box" report. */}
      <div>
        <strong>
          {appointment.displayName} — {appointment.role}
        </strong>
        <small>{appointment.upn}</small>
        {error && <ErrorLine>{error}</ErrorLine>}
        {confirming && (
          <small>
            Revoke {appointment.role} from {appointment.displayName}?
          </small>
        )}
      </div>
      <div className="kombi-admin-row-actions">
        {confirming ? (
          <>
            <QuietAction onClick={() => setConfirming(false)} disabled={busy}>
              Cancel
            </QuietAction>
            <QuietAction onClick={() => void revoke()} disabled={busy}>
              {busy ? 'Revoking…' : 'Confirm revoke'}
            </QuietAction>
          </>
        ) : (
          <QuietAction onClick={() => setConfirming(true)}>Revoke</QuietAction>
        )}
      </div>
    </li>
  );
}

/**
 * The overlay body AdminHome mounts. Fetches its own data on open (useOverlayData → appointmentsLoader),
 * shows the pending line while loading, an ErrorLine for a failed load / ended session, Kombi's own
 * missing-state block for a forbidden result, and the grant form + live list on success — mutations
 * reload() in place.
 */
export function AppointmentsPanel({
  onClose,
  returnFocusRef: _returnFocusRef,
}: {
  onClose: () => void;
  returnFocusRef: ReturnFocusRef;
}) {
  const { data, loading, error, reload } = useOverlayData(appointmentsLoader);

  return (
    <KombiAdminOverlay title="Appointments" onClose={onClose}>
      {loading ? (
        <Spinner label="Loading appointments…" />
      ) : error || (data && data.state === 'signedOut') ? (
        // signedOut inside an overlay: don't navigate — the parent grid owns auth. Just say so.
        <ErrorLine>
          Your session has ended or the appointments could not be loaded. Close this panel and sign
          in again.
        </ErrorLine>
      ) : data && data.state === 'forbidden' ? (
        <NotPermitted what="Appointments" />
      ) : data && data.state === 'ok' ? (
        <div>
          <ProvisionalNote />
          <Notice>
            Record or remove a Reviewer, ChampionRole, SWATMember, Manager or PlatformOwner
            appointment (PC-F19). Every role here is resolved live at the moment of use — nothing is
            inferred from anything self-declared.
          </Notice>

          <SectionHeader>Grant a role</SectionHeader>
          <GrantForm onGranted={reload} />

          <SectionHeader>Live appointments</SectionHeader>
          {data.appointments.length === 0 ? (
            <EmptyState>Nobody currently holds an appointment.</EmptyState>
          ) : (
            <ul className="kombi-admin-rows">
              {data.appointments.map((a) => (
                <AppointmentRow key={a.appointmentId} appointment={a} onRevoked={reload} />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </KombiAdminOverlay>
  );
}

/**
 * Thin route wrapper for the still-registered /admin/appointments deep link. Opens the panel always-
 * open over a blank backdrop; the panel still fetches via useOverlayData, and Close returns to /admin.
 */
export function Appointments() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement | null>(null);
  return <AppointmentsPanel onClose={() => navigate('/admin')} returnFocusRef={ref} />;
}
